/**
 * Subset of GVP Module II rules used for the in-product compliance check.
 * Kept in sync with `skills/psmf-scan/rules.ts`. Back-end scan (when a
 * document is uploaded via /api/compliance/scans) is the source of truth;
 * this client-side check gives QPPVs real-time feedback while authoring.
 */

import type { Psmf, AnnexKey } from './types';

export type Severity = 'critical' | 'major' | 'minor' | 'observation';

export interface PsmfRule {
  id: string;
  reference: string;
  category: string;
  severity: Severity;
  title: string;
  description: string;
  recommendation: string;
  /**
   * Which page section this maps to — used to show the check result inline.
   * Section format: "cover" | "main:<key>" | "annex:<A..I>" | "logbook"
   */
  section: string;
  /** Plain-text keywords for the scan (OR-match, case-insensitive). */
  keywords: string[];
}

export const PSMF_RULES: PsmfRule[] = [
  {
    id: 'PSMF-B1-1',
    reference: 'GVP Module II Section II.B.4.1',
    category: 'QPPV',
    severity: 'critical',
    title: 'QPPV identity disclosed',
    description: 'PSMF must name the QPPV with contact details.',
    recommendation: 'Enter QPPV name, email and phone on the cover page.',
    section: 'cover',
    keywords: ['QPPV', 'Qualified Person', 'contact:'],
  },
  {
    id: 'PSMF-B1-2',
    reference: 'GVP Module II Section II.B.4.1',
    category: 'QPPV',
    severity: 'critical',
    title: 'QPPV deputy or backup documented',
    description: 'A deputy/back-up QPPV arrangement must be documented.',
    recommendation: 'Fill in deputy name and contact in section II.B.4.1.',
    section: 'main:qppv',
    keywords: ['deputy', 'back-up', 'backup', 'alternate QPPV'],
  },
  {
    id: 'PSMF-A-1',
    reference: 'IR 520/2012 Art 7(1)',
    category: 'Registration',
    severity: 'critical',
    title: 'PSMF located within EU/EEA',
    description: 'PSMF must be located in an EU/EEA Member State.',
    recommendation: 'Provide physical address (EU/EEA) in cover-page Location.',
    section: 'cover',
    keywords: ['EU', 'EEA', 'Member State', 'Norway', 'Iceland', 'Liechtenstein', 'address'],
  },
  {
    id: 'PSMF-A-2',
    reference: 'IR 520/2012 Art 26(1)(a)',
    category: 'Registration',
    severity: 'critical',
    title: 'Article 57 / XEVMPD reference',
    description: 'PSMF must carry an EV PSMF reference number from Article 57.',
    recommendation: 'Populate EV PSMF reference number on cover page.',
    section: 'cover',
    keywords: ['Article 57', 'XEVMPD', 'EV PSMF'],
  },
  {
    id: 'PSMF-B2-1',
    reference: 'GVP Module II Section II.B.4.2',
    category: 'Organisation',
    severity: 'critical',
    title: 'Organisational structure described',
    description: 'PSMF must describe PV function, reporting lines and interfaces.',
    recommendation: 'Complete the Organisation section with reporting lines and interfaces.',
    section: 'main:organisation',
    keywords: ['organisational chart', 'organisation chart', 'reporting line', 'interface'],
  },
  {
    id: 'PSMF-B3-1',
    reference: 'GVP Module II Section II.B.4.3',
    category: 'Sources',
    severity: 'critical',
    title: 'Sources of safety data identified',
    description: 'All sources of safety information must be listed.',
    recommendation: 'Tick the source types that apply in the Sources section.',
    section: 'main:sources',
    keywords: ['spontaneous', 'literature', 'clinical trial', 'regulatory authority'],
  },
  {
    id: 'PSMF-B4-1',
    reference: 'GVP Module II Section II.B.4.4',
    category: 'IT Systems',
    severity: 'critical',
    title: 'Safety database identified',
    description: 'Name, version and validation status of the safety database must be specified.',
    recommendation: 'Enter database name, version and validation status.',
    section: 'main:systems',
    keywords: ['safety database', 'database validation'],
  },
  {
    id: 'PSMF-B4-2',
    reference: 'GVP Module II Section II.B.4.4',
    category: 'IT Systems',
    severity: 'major',
    title: 'Computerised system validation (GAMP 5)',
    description: 'Evidence of GAMP 5 validation (IQ/OQ/PQ) for the safety database.',
    recommendation: 'Reference GAMP 5 or IQ/OQ/PQ in the Systems section.',
    section: 'main:systems',
    keywords: ['GAMP 5', 'IQ/OQ/PQ', 'validated system'],
  },
  {
    id: 'PSMF-B5-1',
    reference: 'GVP Module II Section II.B.4.5',
    category: 'Processes',
    severity: 'critical',
    title: 'ICSR processing SOP referenced',
    description: 'ICSR intake, processing and submission SOP must be referenced.',
    recommendation: 'Describe ICSR process and reference the relevant SOP.',
    section: 'main:processes',
    keywords: ['ICSR', 'case processing', 'case intake'],
  },
  {
    id: 'PSMF-B5-2',
    reference: 'GVP Module II Section II.B.4.5',
    category: 'Processes',
    severity: 'critical',
    title: 'Signal detection SOP referenced',
    description: 'Signal detection and management SOP must be referenced.',
    recommendation: 'Describe signal detection and reference SOP.',
    section: 'main:processes',
    keywords: ['signal detection', 'signal management', 'signal validation'],
  },
  {
    id: 'PSMF-B5-3',
    reference: 'GVP Module II Section II.B.4.5',
    category: 'Processes',
    severity: 'major',
    title: 'PSUR / PBRER SOP referenced',
    description: 'PSUR/PBRER preparation and submission SOP.',
    recommendation: 'Mention PSUR or PBRER process.',
    section: 'main:processes',
    keywords: ['PSUR', 'PBRER', 'periodic safety update'],
  },
  {
    id: 'PSMF-B6-1',
    reference: 'GVP Module II Section II.B.4.6',
    category: 'Performance',
    severity: 'major',
    title: 'KPIs defined with targets',
    description: 'KPIs for the PV system with numerical targets.',
    recommendation: 'Add at least one KPI row in Annex F with target.',
    section: 'annex:F',
    keywords: ['compliance rate', 'KPI', 'performance indicator', 'reporting timeliness'],
  },
  {
    id: 'PSMF-B7-1',
    reference: 'GVP Module II Section II.B.4.7',
    category: 'Quality System',
    severity: 'critical',
    title: 'Quality system documented',
    description: 'Quality system covering PV activities documented.',
    recommendation: 'Describe quality system in section II.B.4.7.',
    section: 'main:quality',
    keywords: ['quality system', 'policy', 'training', 'audit'],
  },
  {
    id: 'PSMF-B7-3',
    reference: 'GVP Module II Section II.B.4.7',
    category: 'Quality System',
    severity: 'major',
    title: 'Audit findings note with CAPA',
    description: 'Audit findings with CAPA status and resolution date recorded.',
    recommendation: 'Add audits and CAPA status rows in Annex G.',
    section: 'annex:G',
    keywords: ['CAPA', 'corrective action', 'preventive action'],
  },
  {
    id: 'PSMF-B8-1',
    reference: 'GVP Module II Section II.B.4.2',
    category: 'Delegation',
    severity: 'major',
    title: 'Safety Data Exchange Agreements (SDEAs) listed',
    description: 'All SDEAs with partners / licensees / distributors listed.',
    recommendation: 'Add contract rows in Annex B, type = SDEA.',
    section: 'annex:B',
    keywords: ['SDEA', 'Safety Data Exchange Agreement'],
  },
  {
    id: 'PSMF-C-1',
    reference: 'GVP Module II Section C',
    category: 'Change Control',
    severity: 'major',
    title: 'PSMF change logbook maintained',
    description: 'Logbook listing PSMF changes (version, date, person).',
    recommendation: 'Logbook auto-populates on field edits; make at least one change.',
    section: 'logbook',
    keywords: ['logbook', 'change log', 'revision history'],
  },
  {
    id: 'PSMF-ANNEX-A',
    reference: 'GVP Module II Section II.B.6.1',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex A — QPPV',
    description: 'Annex A must contain QPPV delegated tasks, CV and supplementary contacts.',
    recommendation: 'Populate Annex A delegations and contacts, or mark unused.',
    section: 'annex:A',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-B',
    reference: 'GVP Module II Section II.B.6.1',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex B — Organisational structure',
    description: 'Annex B must list contracts and agreements relevant to PV.',
    recommendation: 'Populate contracts in Annex B, or mark unused.',
    section: 'annex:B',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-C',
    reference: 'GVP Module II Section II.B.6.1',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex C — Sources of safety data',
    description: 'Annex C must list affiliates/studies/programmes over 5-year window.',
    recommendation: 'Populate Annex C sources, or mark unused.',
    section: 'annex:C',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-D',
    reference: 'GVP Module II Section II.B.6.1',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex D — Computerised systems',
    description: 'Annex D must describe computerised systems, or be marked "unused".',
    recommendation: 'Populate Annex D systems, or mark unused.',
    section: 'annex:D',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-E',
    reference: 'GVP Module II Section II.B.6.1 / IR 520/2012 Art 2(5)(a)',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex E — Procedures list',
    description: 'Annex E must list SOPs (id, title, effective date, type).',
    recommendation: 'Add SOP rows in Annex E, or mark unused.',
    section: 'annex:E',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-F',
    reference: 'GVP Module II Section II.B.6.1 / IR 520/2012 Art 9',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex F — Performance indicators',
    description: 'Annex F must list KPIs with targets and current results.',
    recommendation: 'Add at least one KPI in Annex F, or mark unused.',
    section: 'annex:F',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-G',
    reference: 'GVP Module II Section II.B.6.1 / IR 520/2012 Art 3(5)',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex G — Audit schedule and conducted audits',
    description: 'Annex G must include audit schedule and 5-year audit list.',
    recommendation: 'Populate Annex G schedule and audits, or mark unused.',
    section: 'annex:G',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-H',
    reference: 'GVP Module II Section II.B.6.1 / IR 520/2012 Art 3',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex H — Products',
    description: 'Annex H must list products per active substance with authorisation details.',
    recommendation: 'Add product rows in Annex H, or mark unused.',
    section: 'annex:H',
    keywords: [],
  },
  {
    id: 'PSMF-ANNEX-I',
    reference: 'GVP Module II Section II.B.6.1',
    category: 'Annex Structure',
    severity: 'critical',
    title: 'Annex I — Document and record control',
    description: 'Annex I must contain the per-Annex change logbook.',
    recommendation: 'Populate Annex I entries, or mark unused.',
    section: 'annex:I',
    keywords: [],
  },
  {
    id: 'PSMF-FMT-1',
    reference: 'GVP Module II Section II.B.6.1',
    category: 'Format',
    severity: 'minor',
    title: 'Written in English',
    description: 'PSMF should be written in English unless single-MS authorisation.',
    recommendation: 'Set cover-page Language to English.',
    section: 'cover',
    keywords: ['English'],
  },
];

export interface PsmfFinding {
  rule: PsmfRule;
  status: 'pass' | 'fail';
  evidence: string | null;
}

export interface PsmfScanResult {
  findings: PsmfFinding[];
  passedChecks: number;
  failedChecks: number;
  totalChecks: number;
  readinessScore: number; // 0..100, weighted by severity
  summary: string;
}

const SEVERITY_WEIGHT: Record<Severity, number> = {
  critical: 3,
  major: 2,
  minor: 1,
  observation: 0.5,
};

/**
 * Renders the PSMF to a plain-text corpus then matches rule keywords,
 * mirroring the backend skill's keyword pass. Annex-structure rules
 * additionally check the corresponding `annex[key].unused` or row count.
 */
export function scanPsmf(psmf: Psmf): PsmfScanResult {
  const corpus = renderCorpus(psmf).toLowerCase();
  const findings: PsmfFinding[] = [];

  for (const rule of PSMF_RULES) {
    const annexMatch = rule.section.match(/^annex:([A-I])$/);
    let pass = false;
    let evidence: string | null = null;

    if (annexMatch) {
      const key = annexMatch[1] as AnnexKey;
      const node = psmf.annex[key] as { unused: boolean } & Record<string, unknown>;
      if (node.unused) {
        pass = true;
        evidence = `Annex ${key} explicitly marked "unused".`;
      } else {
        const populated = annexHasContent(psmf, key);
        pass = populated;
        evidence = populated ? `Annex ${key} contains content.` : null;
      }
    } else if (rule.section === 'logbook') {
      pass = psmf.logbook.length > 0;
      evidence = pass ? `${psmf.logbook.length} logbook entries.` : null;
    } else if (rule.keywords.length > 0) {
      const hit = rule.keywords.find((kw) => corpus.includes(kw.toLowerCase()));
      pass = Boolean(hit);
      evidence = hit ? `Matched keyword: "${hit}".` : null;
    } else {
      // No keywords and no structural mapping — treat as pass (shouldn't happen).
      pass = true;
    }

    findings.push({ rule, status: pass ? 'pass' : 'fail', evidence });
  }

  const passed = findings.filter((f) => f.status === 'pass').length;
  const failed = findings.length - passed;

  const totalWeight = findings.reduce((a, f) => a + SEVERITY_WEIGHT[f.rule.severity], 0);
  const passedWeight = findings
    .filter((f) => f.status === 'pass')
    .reduce((a, f) => a + SEVERITY_WEIGHT[f.rule.severity], 0);
  const readinessScore = totalWeight > 0 ? Math.round((passedWeight / totalWeight) * 100) : 0;

  const criticalFails = findings.filter((f) => f.status === 'fail' && f.rule.severity === 'critical').length;
  const summary = criticalFails > 0
    ? `${criticalFails} critical gap(s) remain. Readiness ${readinessScore}%.`
    : failed === 0
      ? `All ${findings.length} checks passed. Readiness ${readinessScore}%.`
      : `${passed}/${findings.length} checks passed. Readiness ${readinessScore}%.`;

  return {
    findings,
    passedChecks: passed,
    failedChecks: failed,
    totalChecks: findings.length,
    readinessScore,
    summary,
  };
}

function annexHasContent(psmf: Psmf, key: AnnexKey): boolean {
  const a = psmf.annex;
  switch (key) {
    case 'A':
      return a.A.delegations.length > 0 || a.A.contacts.length > 0 || a.A.cvReference.trim().length > 0;
    case 'B':
      return a.B.contracts.length > 0;
    case 'C':
      return a.C.sources.length > 0;
    case 'D':
      return a.D.systems.length > 0;
    case 'E':
      return a.E.sops.length > 0;
    case 'F':
      return a.F.kpis.length > 0 || a.F.timeliness15Day.trim().length > 0 || a.F.timeliness90Day.trim().length > 0;
    case 'G':
      return a.G.audits.length > 0 || a.G.schedule.trim().length > 0;
    case 'H':
      return a.H.products.length > 0;
    case 'I':
      return a.I.entries.length > 0;
  }
}

function renderCorpus(psmf: Psmf): string {
  const parts: string[] = [];
  const c = psmf.cover;
  parts.push(c.mahName, c.qppvName, c.qppvEmail, c.qppvPhone, c.qppvAltContact, c.evPsmfRef, c.location, c.language);
  const m = psmf.main;
  parts.push(
    m.qppv.deputyName, m.qppv.deputyContact, m.qppv.delegationSummary, m.qppv.notificationTriggers,
    m.organisation.chartDescription, m.organisation.reportingLines, m.organisation.interfaces, m.organisation.sites,
    m.sources.notes,
    m.systems.safetyDbName, m.systems.safetyDbVersion, m.systems.validationStatus,
    m.systems.fitnessForPurpose, m.systems.changeControl, m.systems.backup,
    m.processes.icsrProcess, m.processes.signalDetection, m.processes.psurProcess,
    m.processes.riskBenefit, m.processes.safetyCommunication, m.processes.smpcVariations,
    m.performance.kpiSummary, m.performance.complianceRate, m.performance.variationTracking,
    m.quality.qualitySystem, m.quality.training, m.quality.auditProgramme, m.quality.deviationManagement,
  );

  // Source checkbox to keyword
  const s = m.sources;
  if (s.spontaneous) parts.push('spontaneous');
  if (s.clinicalTrials) parts.push('clinical trial');
  if (s.literature) parts.push('literature');
  if (s.regulatoryAuthorities) parts.push('regulatory authority');
  if (s.partners) parts.push('partners, licensee');
  if (s.patientSupportProgrammes) parts.push('patient support programme');
  if (s.socialMedia) parts.push('social media');

  for (const d of psmf.annex.A.delegations) parts.push(d.task, d.delegatedTo, d.oversight);
  for (const cx of psmf.annex.A.contacts) parts.push(cx.name, cx.role, cx.email, cx.phone);
  for (const b of psmf.annex.B.contracts) parts.push(b.counterparty, b.type, b.scope, b.status);
  for (const src of psmf.annex.C.sources) parts.push(src.kind, src.name, src.country);
  for (const sys of psmf.annex.D.systems) parts.push(sys.name, sys.version, sys.validationStatus, sys.fitnessForPurpose);
  for (const sop of psmf.annex.E.sops) parts.push(sop.docId, sop.title, sop.docType);
  for (const k of psmf.annex.F.kpis) parts.push(k.name, k.target, k.currentResult);
  for (const au of psmf.annex.G.audits) parts.push(au.scope, au.capaStatus, au.significantFindings);
  for (const p of psmf.annex.H.products) parts.push(p.activeSubstance, p.inn, p.authorisationNumber, p.procedureType);
  for (const e of psmf.annex.I.entries) parts.push(e.annex, e.change, e.user);

  return parts.filter(Boolean).join('\n');
}
